/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailCollabActions.js
 * @description
 * UI-facing action helpers for shared-mailbox collaboration: assigning with the takeover
 * confirmation (backend answers 409 + `currentAssigneeId` when somebody else owns the thread) and a
 * uniform error toast. Confirmations use the premium {@link HappyAlert} dialog, never a native one.
 */

import Toast from 'react-native-toast-message';
import HappyAlert from '../components/HappyAlert';
import {userId} from './mailCollabUtils';

/**
 * Shows the "Take over from <name>?" confirmation.
 *
 * @param {Function} t i18n translator.
 * @param {string} name Current assignee display name.
 * @returns {Promise<boolean>} True when the user confirmed.
 */
export const confirmTakeover = (t, name) => new Promise((resolve) => {
    HappyAlert.show({
        type: 'question',
        title: t('mail_collab_takeover_title', {name: name || t('mail_collab_someone')}),
        message: t('mail_collab_takeover_text'),
        buttons: [
            {text: t('cancel'), style: 'cancel', onPress: () => resolve(false)},
            {text: t('mail_collab_takeover_confirm'), onPress: () => resolve(true)},
        ],
    });
});

/**
 * Toasts a failed collaboration call (401/403 are mapped to the generic "no permission" copy).
 *
 * @param {Function} t i18n translator.
 * @param {Error} error Failure.
 */
export const toastCollabError = (t, error) => {
    Toast.show({
        type: 'error',
        text1: t('mail_error'),
        text2: error?.status === 403 ? t('mail_collab_no_permission') : error?.message,
    });
};

/**
 * Assigns a thread; on a takeover 409 asks the user and retries once with `takeover: true`.
 *
 * @param {Object} params Parameters.
 * @param {Object} params.collab `useMailCollab()` result.
 * @param {string} params.address Shared mailbox address.
 * @param {string} params.conversationKey Conversation key.
 * @param {string} params.assigneeId New assignee id.
 * @param {string} [params.subject] Thread subject (for the notification text).
 * @param {Object[]} [params.members] Known members (to name the current assignee).
 * @param {Object|null} [params.state] Current thread state.
 * @param {Function} params.t i18n translator.
 * @returns {Promise<Object|null>} Updated state, or null when cancelled / failed (toast shown).
 */
export const assignWithTakeover = async ({collab, address, conversationKey, assigneeId, subject, members = [], state = null, t}) => {
    try {
        return await collab.assign(address, conversationKey, assigneeId, {subject});
    } catch (error) {
        if (error?.status !== 409) {
            toastCollabError(t, error);

            return null;
        }

        const currentId = String(error?.payload?.currentAssigneeId || userId(state?.assignee));
        const current = (members || []).find((member) => userId(member) === currentId) || state?.assignee || null;
        const confirmed = await confirmTakeover(t, current?.name);

        if (!confirmed) {
            return null;
        }

        try {
            return await collab.assign(address, conversationKey, assigneeId, {subject, takeover: true});
        } catch (retryError) {
            toastCollabError(t, retryError);

            return null;
        }
    }
};
